import React, { useState } from 'react';
import cn from 'classnames';
import get from 'lodash/get';
import intersection from 'lodash/intersection';

import { setMathJaxTypeset } from 'chameleon/src/utility/utils';
import Popover from 'chameleon/ui-stack/ui/base/popover';
import LearnosityText from 'legacy/src/components/learnosity/LearnosityText';
import Icon from 'chameleon/ui-stack/ui/base/icon';

// eslint-disable-next-line no-invalid-regexp
const replaceResponses = new RegExp('{{response}}', 'dg');
const SPAnswerStatus = {
  Submitted: 'submitted',
  Unsubmitted: 'unsubmitted',
  NotStarted: 'not-started',
  NotPresent: 'not-present',
};
function buildClozedropdownAnswer(question, tipResponse) {
  let answer = question.template.split(replaceResponses);
  // Build the tip answer using the  Learnosity question template.
  // And fortunately for us, length({{response}}) === length(tipResponse),
  // but not in the right order. So we'll use learnosity `possible_responses`
  // list attached to each question.
  // Further this code intersets two arrays at even indices (2i + 1)
  question.possible_responses.forEach((possibleResponses, i) => {
    let [selectedAnswer] = intersection(possibleResponses, tipResponse);
    if (!selectedAnswer) {
      selectedAnswer = ' ';
    }
    answer.splice(
      2 * i + 1,
      0,
      `<strong class='p-1 border'>${selectedAnswer}</strong>`,
    );
  });
  return answer.join('');
}

const AnswerPopOver = (props) => {
  const { label, status, children } = props;
  const [popOnHover, setPopOnHover] = useState(true);

  if ([SPAnswerStatus.NotStarted, SPAnswerStatus.NotPresent].includes(status)) {
    return <div />;
  }

  // Render response by default
  let content = children;
  if (status === SPAnswerStatus.Unsubmitted) {
    content = 'The student has not yet submitted this assignment.';
  }

  return (
    <Popover
      popOnHover={popOnHover}
      withPortal
      size="medium"
      position="right-center"
      fallBackPlacements={[
        'left-center',
        'right-center',
        'left-start',
        'left-end',
        'right-start',
        'right-end',
      ]}
      buttonClassName="w-full"
      contentClassName="leading-normal SPPStudentPerformanceAnswer"
      trigger={(buttonProps, _, reference) => (
        <div
          {...buttonProps}
          ref={reference}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          className={cn({
            'w-full cursor-pointer': true,
            'text-purple-600': !popOnHover,
            'text-gray-800 hover:text-purple-600':
              status === SPAnswerStatus.Submitted,
            'text-gray-300': status === SPAnswerStatus.Unsubmitted,
          })}
        >
          <Icon name="checkmark" onClick={() => setPopOnHover(false)} />
        </div>
      )}
      accessibility={{
        role: 'button',
        'aria-label': label,
      }}
      focusOrder={['floating', 'content', 'reference']}
      onClose={() => setPopOnHover(true)}
      onContentMount={(contentElementRef) => {
        setMathJaxTypeset(contentElementRef.current);
      }}
    >
      {content}
    </Popover>
  );
};

const SPPStudentPerformanceAnswers = (props) => {
  const {
    student,
    parts,
    sessionTemplateItemsByRef,
    studentResponses,
    studentSessionsByUserId,
  } = props;

  const submitted = Boolean(
    get(studentSessionsByUserId, `[${student.id}].submittedAt`, false),
  );

  const getSPAnswerStatus = (answerReferenceId) => {
    // By default don't show anything
    let answerStatus = SPAnswerStatus.NotStarted;

    // If there are student responses it means that user started the assignment.
    // But a further check will state if the answer can be seen by teacher.
    if (get(studentResponses, `[${student.id}]`, null)) {
      answerStatus = SPAnswerStatus.Unsubmitted;
    }

    if (submitted) {
      answerStatus = SPAnswerStatus.Submitted;
    }

    const answerData = get(
      studentResponses,
      `[${student.id}][${answerReferenceId}]`,
      get(
        studentResponses,
        `[${student.id}].questions[${answerReferenceId}]`,
        null,
      ),
    );
    // If there isn't an answer, don't show any popover
    if (!answerData) {
      answerStatus = SPAnswerStatus.NotPresent;
    }

    return answerStatus;
  };

  const answers = parts.reduce((list, item) => {
    const label = `${student.firstName} ${student.lastName} Completed ${item.title} Answer`;
    list.push(
      <td
        key={item.learnosityItemReference}
        className="td_answer_answered_indicators"
      >
        <AnswerPopOver
          label={label}
          status={getSPAnswerStatus(item.learnosityItemReference)}
        >
          <LearnosityText
            text={get(
              studentResponses,
              `[${student.id}][${item.learnosityItemReference}]`,
              '',
            )}
          />
        </AnswerPopOver>
      </td>,
    );

    const itemQuestions = get(
      sessionTemplateItemsByRef,
      `[${item.learnosityItemReference}].questions`,
      [],
    );

    itemQuestions.forEach((question, i) => {
      const { type, response_id } = question || {};
      if (type !== 'formulaessayV2' && type !== 'clozedropdown') return;
      const tipResponse = get(
        studentResponses,
        `[${student.id}].questions[${response_id}]`,
        0,
      );
      const tipLabel = `${student.firstName} ${student.lastName} Completed ${item.title} Tip #${i}`;
      let answer = tipResponse;
      if (tipResponse.length && type === 'clozedropdown') {
        answer = buildClozedropdownAnswer(question, tipResponse);
      }

      list.push(
        <td key={response_id} className="td_tip_filled_indicators">
          <AnswerPopOver
            label={tipLabel}
            status={getSPAnswerStatus(response_id)}
          >
            <LearnosityText text={answer} />
          </AnswerPopOver>
        </td>,
      );
    });

    return list;
  }, []);

  return <React.Fragment>{answers}</React.Fragment>;
};

export default SPPStudentPerformanceAnswers;
